import React from 'react';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { icons } from '../../themes/icons';
import {
  GLOBAL_SEARCH_MIN_CHARS,
  GlobalSearchGroupKey,
  GlobalSearchItem,
  useGlobalSearch,
  useGlobalSearchScope,
} from './useGlobalSearch';

const GROUP_ICONS: Record<GlobalSearchGroupKey, React.ElementType> = {
  users: icons.PersonIcon,
  businesses: icons.StorefrontIcon,
  offers: icons.LocalOfferIcon,
  reservations: icons.EventAvailableIcon,
  content: icons.CollectionsIcon,
};

/** Singular badge shown on every row so a result's entity is never ambiguous. */
const GROUP_BADGES: Record<GlobalSearchGroupKey, string> = {
  users: 'User',
  businesses: 'Business',
  offers: 'Offer',
  reservations: 'Reservation',
  content: 'Content',
};

/** Placeholder wording follows the role: a social admin only searches content. */
const GROUP_PLACEHOLDERS: Record<GlobalSearchGroupKey, string> = {
  users: 'users',
  businesses: 'businesses',
  offers: 'offers',
  reservations: 'reservations',
  content: 'content',
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Bolds the typed term inside a result label so the match is obvious. */
const Highlight: React.FC<{ text: string; term: string }> = ({ text, term }) => {
  const search = term.trim();

  if (!search) return <>{text}</>;

  const parts = text.split(new RegExp(`(${escapeRegExp(search)})`, 'ig'));

  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === search.toLowerCase() ? (
          <Mark key={index}>{part}</Mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};

export const GlobalSearch: React.FC = () => {
  const history = useHistory();
  const scope = useGlobalSearchScope();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);

  const [term, setTerm] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const { groups, items, loading } = useGlobalSearch(term);

  const hasTerm = term.trim().length >= GLOBAL_SEARCH_MIN_CHARS;

  // Reset on a new term, not on `items`: groups stream in one by one and the
  // highlighted row must not jump around while they land.
  React.useEffect(() => setActiveIndex(0), [term]);

  React.useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Cmd/Ctrl+K focuses the search from anywhere in the admin.
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const goTo = React.useCallback(
    (item: GlobalSearchItem) => {
      setOpen(false);
      setTerm('');
      inputRef.current?.blur();
      history.push(item.url);
    },
    [history],
  );

  const moveActive = React.useCallback(
    (delta: number) => {
      if (items.length === 0) return;

      setActiveIndex((current) => {
        const next = (current + delta + items.length) % items.length;
        listRef.current
          ?.querySelector(`[data-index="${next}"]`)
          ?.scrollIntoView({ block: 'nearest' });
        return next;
      });
    },
    [items.length],
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setOpen(true);
        moveActive(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        moveActive(-1);
      } else if (event.key === 'Enter') {
        const item = items[activeIndex];
        if (item) {
          event.preventDefault();
          goTo(item);
        }
      } else if (event.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    },
    [activeIndex, goTo, items, moveActive],
  );

  const placeholder = React.useMemo(
    () => `Search ${scope.groupKeys.map((key) => GROUP_PLACEHOLDERS[key]).join(', ')}...`,
    [scope.groupKeys],
  );

  // Roles with nothing to search (venue portal, fast check) get no searchbar at all.
  if (!scope.enabled) return null;

  let flatIndex = -1;

  return (
    <Container ref={containerRef}>
      <InputWrapper focused={open}>
        <icons.SearchIconV2 fontSize="small" />
        <Input
          ref={inputRef}
          value={term}
          placeholder={placeholder}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {term ? (
          <ClearButton
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setTerm('');
              inputRef.current?.focus();
            }}
          >
            <icons.CrossBlackIcon style={{ width: 10, height: 10 }} />
          </ClearButton>
        ) : (
          <Shortcut>⌘K</Shortcut>
        )}
      </InputWrapper>

      {open && hasTerm && (
        <Dropdown ref={listRef}>
          {loading && groups.length === 0 && <Message>Searching...</Message>}

          {!loading && groups.length === 0 && (
            <Message>
              No results for <strong>{term.trim()}</strong>
            </Message>
          )}

          {groups.map((group) => {
            const GroupIcon = GROUP_ICONS[group.key];

            return (
              <Group key={group.key}>
                <GroupHeader>
                  <GroupIcon style={{ fontSize: 15 }} />
                  <span>{group.label}</span>
                  <GroupCount>{group.total ?? group.items.length}</GroupCount>
                  {group.total !== undefined && group.total > group.items.length && (
                    <GroupHint>showing first {group.items.length}</GroupHint>
                  )}
                </GroupHeader>

                {group.items.map((item) => {
                  flatIndex += 1;
                  const index = flatIndex;

                  return (
                    <Row
                      key={item.key}
                      data-index={index}
                      active={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => goTo(item)}
                    >
                      <Avatar rounded={item.group !== 'users' && item.group !== 'reservations'}>
                        {item.picture ? (
                          <img src={item.picture} alt="" />
                        ) : (
                          <AvatarFallback>{item.title.charAt(0).toUpperCase()}</AvatarFallback>
                        )}
                      </Avatar>

                      <RowText>
                        <RowTitle>
                          <Highlight text={item.title} term={term} />
                          <TypeBadge group={item.group}>{GROUP_BADGES[item.group]}</TypeBadge>
                        </RowTitle>
                        {item.subtitle && (
                          <RowSubtitle>
                            <Highlight text={item.subtitle} term={term} />
                          </RowSubtitle>
                        )}
                        {item.meta && <RowMeta>{item.meta}</RowMeta>}
                      </RowText>

                      {item.status && <Status status={item.status}>{item.status}</Status>}
                    </Row>
                  );
                })}
              </Group>
            );
          })}

          {loading && groups.length > 0 && <Searching>Searching the rest...</Searching>}

          {groups.length > 0 && (
            <Footer>
              <span>
                <kbd>↑</kbd>
                <kbd>↓</kbd> to navigate
              </span>
              <span>
                <kbd>Enter</kbd> to open
              </span>
              <span>
                <kbd>Esc</kbd> to close
              </span>
            </Footer>
          )}
        </Dropdown>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  margin-left: 0.75rem;
`;

const InputWrapper = styled.div<{ focused: boolean }>`
  ${({ focused, theme }) => `
  align-items: center;
  background: ${theme.colors.white};
  border: 1px solid ${focused ? theme.colors.brandBlue : theme.colors.brandLightGrey};
  border-radius: 8px;
  display: flex;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.6rem;
  transition: width 120ms ease-in-out;
  width: ${focused ? '420px' : '320px'};

  > svg {
    color: ${theme.colors.brandGrey};
    flex: 0 0 auto;
  }
`}
`;

const Input = styled.input`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.brandDarkGrey};
  flex: 1 1 auto;
  font-family: 'Poppins', sans-serif;
  font-size: 13px;
  font-weight: 500;
  min-width: 0;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.brandGrey};
    font-weight: 400;
  }
`;

const ClearButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 0;
`;

const Shortcut = styled.span`
  ${({ theme }) => `
  background: ${theme.colors.lightGrey1};
  border: 1px solid ${theme.colors.brandLightGrey};
  border-radius: 4px;
  color: ${theme.colors.brandGrey};
  font-size: 10px;
  font-weight: 600;
  padding: 1px 4px;
`}
`;

const Dropdown = styled.div`
  ${({ theme }) => `
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.brandLightGrey};
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
  left: 0;
  max-height: 70vh;
  overflow-y: auto;
  padding: 0.35rem 0;
  position: absolute;
  top: calc(100% + 8px);
  width: 480px;
  z-index: 10;
`}
`;

const Message = styled.div`
  color: ${({ theme }) => theme.colors.brandMediumGrey};
  font-size: 13px;
  padding: 0.9rem 0.9rem;
  text-align: center;
`;

const Group = styled.div`
  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.lightGrey2};
  }
`;

const GroupHeader = styled.div`
  ${({ theme }) => `
  align-items: center;
  color: ${theme.colors.brandMediumGrey};
  display: flex;
  font-size: 11px;
  font-weight: 600;
  gap: 0.35rem;
  letter-spacing: 0.04em;
  padding: 0.5rem 0.75rem 0.25rem;
  text-transform: uppercase;
`}
`;

const GroupCount = styled.span`
  ${({ theme }) => `
  background: ${theme.colors.lightGrey1};
  border-radius: 8px;
  color: ${theme.colors.brandMediumGrey};
  font-size: 10px;
  padding: 0 5px;
`}
`;

const Searching = styled.div`
  color: ${({ theme }) => theme.colors.brandGrey};
  font-size: 11px;
  padding: 0.4rem 0.75rem 0.1rem;
`;

const GroupHint = styled.span`
  color: ${({ theme }) => theme.colors.brandGrey};
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
`;

const Row = styled.div<{ active: boolean }>`
  ${({ active, theme }) => `
  align-items: center;
  background: ${active ? theme.colors.lightGrey1 : 'transparent'};
  cursor: pointer;
  display: flex;
  gap: 0.6rem;
  padding: 0.4rem 0.75rem;
`}
`;

const Avatar = styled.div<{ rounded: boolean }>`
  ${({ rounded, theme }) => `
  align-items: center;
  background: ${theme.colors.lightGrey1};
  border-radius: ${rounded ? '6px' : '50%'};
  display: flex;
  flex: 0 0 auto;
  height: 34px;
  justify-content: center;
  overflow: hidden;
  width: 34px;

  img {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }
`}
`;

const AvatarFallback = styled.span`
  color: ${({ theme }) => theme.colors.brandMediumGrey};
  font-size: 13px;
  font-weight: 600;
`;

const RowText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const ellipsis = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowTitle = styled.div`
  ${ellipsis}
  align-items: center;
  color: ${({ theme }) => theme.colors.brandDarkGrey};
  display: flex;
  font-size: 13px;
  font-weight: 600;
  gap: 0.35rem;
`;

const TypeBadge = styled.span<{ group: GlobalSearchGroupKey }>`
  ${({ group, theme }) => {
    const colors: Record<GlobalSearchGroupKey, string> = {
      users: theme.colors.brandBlue,
      businesses: theme.colors.brandCyan,
      offers: theme.colors.brandPink,
      reservations: theme.colors.brandOrange,
      content: theme.colors.brandGreen,
    };

    return `
    background: ${colors[group]}1a;
    border-radius: 4px;
    color: ${colors[group]};
    flex: 0 0 auto;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.03em;
    padding: 1px 5px;
    text-transform: uppercase;
  `;
  }}
`;

const RowSubtitle = styled.div`
  ${ellipsis}
  color: ${({ theme }) => theme.colors.brandMediumGrey};
  font-size: 12px;
`;

const RowMeta = styled.div`
  ${ellipsis}
  color: ${({ theme }) => theme.colors.brandGrey};
  font-size: 11px;
`;

const Status = styled.span<{ status: string }>`
  ${({ status, theme }) => {
    const normalized = status.toLowerCase();
    const color = ['active', 'checkedin', 'approved', 'reserved'].includes(normalized)
      ? theme.colors.brandGreen
      : ['pending', 'inreview', 'draft'].includes(normalized)
        ? theme.colors.brandOrange
        : ['rejected', 'noshow', 'cancelled', 'suspended', 'blocked'].includes(normalized)
          ? theme.colors.brandRed
          : theme.colors.brandMediumGrey;

    return `
    background: ${color}1a;
    border-radius: 10px;
    color: ${color};
    flex: 0 0 auto;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    text-transform: capitalize;
  `;
  }}
`;

const Mark = styled.mark`
  background: #fff2a8;
  color: inherit;
  padding: 0;
`;

const Footer = styled.div`
  ${({ theme }) => `
  border-top: 1px solid ${theme.colors.lightGrey2};
  color: ${theme.colors.brandGrey};
  display: flex;
  font-size: 10px;
  gap: 0.9rem;
  margin-top: 0.25rem;
  padding: 0.45rem 0.75rem 0.1rem;

  kbd {
    background: ${theme.colors.lightGrey1};
    border: 1px solid ${theme.colors.brandLightGrey};
    border-radius: 3px;
    font-family: inherit;
    margin-right: 2px;
    padding: 0 3px;
  }
`}
`;
